import React, { useRef, useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { applicationsApi } from '../services/api-services';
import { resolveUrl } from '../utils/url';
import HelpSidebar from '../components/HelpSidebar';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

const LOADING_MSG = '正在处理中...';

const ApplicationStandalone: React.FC = () => {
  const { slug: id } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const token = searchParams.get('token') || localStorage.getItem('token') || '';
  const theme = searchParams.get('theme') || 'light';

  // id can be numeric app id or slug
  const isNumeric = /^\d+$/.test(id || '');
  const [resolvedId, setResolvedId] = useState<string | null>(isNumeric ? (id || null) : null);
  const [isLoading, setIsLoading] = useState(!isNumeric);
  const staticUrl = resolvedId
    ? `${API_BASE}/sub_app/${resolvedId}/static/index.html?theme=${theme}`
    : '';

  const [pluginLoading, setPluginLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('正在处理中...');
  const [thinkingText, setThinkingText] = useState('');
  const [toolCallText, setToolCallText] = useState('');
  const [dotCount, setDotCount] = useState(1);
  const [appName, setAppName] = useState('');
  const [appIcon, setAppIcon] = useState('');

  // Verify token and resolve slug to id if needed
  useEffect(() => {
    if (!token) {
      navigate(`/app/${id}/login`);
      return;
    }
    const fetchApp = isNumeric
      ? applicationsApi.get(Number(id))
      : applicationsApi.getBySlug(id!);
    fetchApp.then((res: any) => {
      setAppName(res.name || '');
      setAppIcon(res.icon || '');
      document.title = res.name || '应用';
      if (!isNumeric) setResolvedId(String(res.id));
      setIsLoading(false);
    }).catch(() => navigate(`/app/${id}/login`));
  }, [id, token, isNumeric, navigate]);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (e.data?.type === 'plugin-loading') {
        if (e.data.action === 'show') {
          setPluginLoading(true);
          setLoadingMessage(e.data.message || '正在处理中...');
          setThinkingText('');
          setToolCallText('');
          setDotCount(1);
        } else if (e.data.action === 'update') {
          const msg = e.data.message || '';
          setLoadingMessage(msg);
          if (msg.startsWith('💭')) {
            const content = msg.replace('💭 ', '');
            setThinkingText(prev => prev + content);
          } else if (msg.startsWith('🔧')) {
            setToolCallText(msg.replace('🔧 ', ''));
          }
        } else if (e.data.action === 'hide') {
          setPluginLoading(false);
          setThinkingText('');
          setToolCallText('');
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!pluginLoading) return;
    const dotTimer = setInterval(() => setDotCount(d => d >= 3 ? 1 : d + 1), 500);
    return () => clearInterval(dotTimer);
  }, [pluginLoading]);

  const [currentTheme, setCurrentTheme] = useState(theme);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const toggleTheme = () => {
    const next = currentTheme === 'light' ? 'glass' : 'light';
    setCurrentTheme(next);
    iframeRef.current?.contentWindow?.postMessage({ type: 'theme-change', theme: next }, '*');
    // Persist theme in URL so refresh restores the selected theme
    const url = new URL(window.location.href);
    url.searchParams.set('theme', next);
    window.history.replaceState(null, '', url.toString());
  };

  useEffect(() => {
    if (!userMenuOpen) return;
    const close = () => setUserMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [userMenuOpen]);

  return (
    <div style={{ width: '100vw', height: '100vh', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        height: 56, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '0 24px',
        background: currentTheme === 'glass'
          ? 'radial-gradient(circle at 16% 0%, rgba(129,140,248,0.22), transparent 30%), linear-gradient(90deg, rgba(10,15,30,0.86), rgba(15,23,42,0.76))'
          : 'radial-gradient(circle at 18% 0%, rgba(0,113,227,0.08), transparent 28%), linear-gradient(90deg, rgba(255,255,255,0.92), rgba(247,250,255,0.84))',
        borderBottom: `0.5px solid ${currentTheme === 'glass' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.07)'}`,
        boxShadow: currentTheme === 'glass' ? '0 10px 28px rgba(0,0,0,0.18)' : '0 8px 24px rgba(15,23,42,0.035)',
        backdropFilter: 'saturate(180%) blur(20px)',
        WebkitBackdropFilter: 'saturate(180%) blur(20px)',
      }}>
        {appIcon && !appIcon.startsWith('http') && !appIcon.startsWith('data:') && !appIcon.startsWith('/uploads/') && !appIcon.startsWith('/avatars/') && !appIcon.startsWith('/app-icons/') && <span style={{ fontSize: 20 }}>{appIcon}</span>}
        {appIcon && (appIcon.startsWith('http') || appIcon.startsWith('data:') || appIcon.startsWith('/uploads/') || appIcon.startsWith('/avatars/') || appIcon.startsWith('/app-icons/')) && <img src={resolveUrl(appIcon)} alt="" style={{ width: 22, height: 22, objectFit: 'contain', borderRadius: 4 }} />}
        <span style={{ fontSize: 18, fontWeight: 600, color: currentTheme === 'glass' ? 'rgba(255,255,255,0.9)' : '#1d1d1f' }}>{appName}</span>
        <div style={{ flex: 1 }} />
        <button onClick={toggleTheme} title={`切换到${currentTheme === 'light' ? '玻璃' : '明亮'}主题`} style={{
          width: 38, height: 38, border: 'none', borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'transparent',
          color: currentTheme === 'glass' ? 'rgba(255,255,255,0.7)' : '#555',
        }}>
          {currentTheme === 'light' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>
        <div style={{ position: 'relative' }}>
          <button
            onClick={(e) => { e.stopPropagation(); setUserMenuOpen(!userMenuOpen); }}
            title="用户菜单"
            style={{
              width: 38, height: 38, border: 'none', borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent',
              color: currentTheme === 'glass' ? 'rgba(255,255,255,0.7)' : '#555',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </button>
          {userMenuOpen && (
            <div style={{
              position: 'absolute', top: 44, right: 0, minWidth: 140, borderRadius: 12, overflow: 'hidden', zIndex: 1000,
              background: currentTheme === 'glass' ? 'rgba(30,35,55,0.92)' : 'rgba(255,255,255,0.94)',
              border: `0.5px solid ${currentTheme === 'glass' ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.07)'}`,
              boxShadow: currentTheme === 'glass' ? '0 14px 34px rgba(0,0,0,0.32)' : '0 14px 34px rgba(15,23,42,0.12)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
            }}>
              <div onClick={() => { setUserMenuOpen(false); localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href = `/app/${id}/login`; }} style={{
                padding: '10px 16px', cursor: 'pointer', fontSize: 14,
                color: currentTheme === 'glass' ? 'rgba(255,255,255,0.85)' : '#333',
              }}>退出系统</div>
            </div>
          )}
        </div>
      </div>
      {staticUrl && (
        <iframe
          ref={iframeRef}
          src={staticUrl}
          onLoad={() => iframeRef.current?.contentWindow?.postMessage({ type: 'auth-token', token }, '*')}
          style={{ width: '100%', flex: 1, border: 'none', display: 'block' }}
          title="应用页面"
        />
      )}
      {pluginLoading && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(10,12,20,0.75)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            width: 650, height: 500,
            background: 'rgba(30,30,45,0.95)', border: '1px solid rgba(99,179,237,0.2)',
            borderRadius: 16, backdropFilter: 'blur(12px)', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{
              padding: '16px 20px', borderBottom: '1px solid rgba(99,179,237,0.15)',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{
                width: 16, height: 16, borderRadius: '50%',
                border: '2px solid rgba(99,179,237,0.3)', borderTopColor: '#63b3ed',
                animation: 'spin 1s linear infinite',
              }} />
              <span style={{ color: 'rgba(147,197,253,0.95)', fontSize: 14, fontWeight: 500 }}>
                思考中...
              </span>
            </div>
            <div style={{
              flex: 1, padding: '20px', overflowY: 'auto',
              color: 'rgba(255,255,255,0.85)', fontSize: 13, lineHeight: 1.6,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}
            ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}>
              {thinkingText || '准备中...'}
            </div>
            {toolCallText && (
              <div style={{
                padding: '14px 20px', borderTop: '1px solid rgba(99,179,237,0.15)',
                color: 'rgba(147,197,253,0.9)', fontSize: 12,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{ opacity: 0.7, flexShrink: 0 }}>工具执行:</span>
                <span style={{ 
                  flex: 1, 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>{toolCallText}</span>
              </div>
            )}
          </div>
          <style>{`
            @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}
      <HelpSidebar />
    </div>
  );
};

export default ApplicationStandalone;
